import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ActiveUserStore } from './user/active-user.state';
import { RoleStore } from './role/role.state';
import { PermissionWorkspaceMap, User, UserWorkspace } from '../models/user';
import { Access, mergeAccessList, Role } from '../models/rbac';
import { WorkspaceStore } from './workspace/workspace.state';
import { Workspace, WorkspaceTypeKey } from '../models/workspace';
import { CategoryStore } from './category/category.state';
import { TopicStore } from './topic/topic.state';
import { BookmarkCategoryStore } from './bookmark-category/bookmark-category.state';
import { DataLayerTypeStore } from './data-layer-type/data-layer-type.state';
import { computed, inject } from '@angular/core';
import { mergeArrays } from '../utility/array.utility';
import { Category } from '../models/category';
import { Topic } from '../models/topic';
import { BookmarkCategory } from '../models/bookmark-category';
import { DataLayerType } from '../models/data-layer-type';

export type GlobalsState = {
  user: User | null;
  permissions: PermissionWorkspaceMap;
};

const initialState: GlobalsState = {
  user: null,
  permissions: {},
};

export const PermissionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(
    (
      store,
      activeUserStore = inject(ActiveUserStore),
      categoryStore = inject(CategoryStore),
      topicStore = inject(TopicStore),
      bookmarkCategoryStore = inject(BookmarkCategoryStore),
      dataLayerTypeStore = inject(DataLayerTypeStore)
    ) => ({
      workspaceTypeMapping: computed(() => {
        const mapping: Record<string, Set<WorkspaceTypeKey>> = {};

        const addMapping = (wsIds: string[], type: WorkspaceTypeKey) => {
          wsIds?.forEach(id => {
            if (!mapping[id]) mapping[id] = new Set();
            mapping[id].add(type);
          });
        };

        // Indicator Categories
        Object.values(categoryStore.items()).forEach((cat: Category) =>
          addMapping(cat.workspaces || [], 'indicatorCategory')
        );
        // Topics
        Object.values(topicStore.items()).forEach((topic: Topic) =>
          addMapping(topic.workspaces || [], 'topic')
        );
        // Bookmark Categories
        Object.values(bookmarkCategoryStore.items()).forEach(
          (bc: BookmarkCategory) =>
            addMapping(bc.workspaces || [], 'bookmarkCategory')
        );
        // Data Layer Types
        Object.values(dataLayerTypeStore.items()).forEach(
          (dlt: DataLayerType) => {
            const type: WorkspaceTypeKey =
              dlt.type === 'Polygon' ? 'dataLayerType' : 'poiDataLayerType';
            addMapping(dlt.workspaces || [], type);
          }
        );
        return mapping;
      }),
      canAddAnything: computed(() => {
        const user = activeUserStore.user();
        if (!user) return false;
        if (user.isAdmin || user.isSuperAdmin) return true;

        const permissions = store.permissions();

        // Simply check if any permission has add=true
        return Object.values(permissions).some(access => access.add);
      }),
    })
  ),
  withMethods(
    (
      store,
      activeUserStore = inject(ActiveUserStore),
      roleStore = inject(RoleStore),
      wsStore = inject(WorkspaceStore)
    ) => ({
      userWorkspaces() {
        const wsIds = activeUserStore
          .user()
          ?.workspaces.map(ws => ws.workspace);
        if (!wsIds || !wsIds.length) return [];
        return wsStore.getByIds(wsIds).filter(ws => !!ws);
      },
      setPermissions() {
        const permissions: PermissionWorkspaceMap = {};
        const user = activeUserStore.user();
        if (user) {
          user.workspaces.forEach((workspace: UserWorkspace) => {
            permissions[workspace.workspace] = mergeAccessList(
              roleStore.getByIds(workspace.roles).map((role: Role) => {
                return role.accessList;
              })
            );
          });
        }
        patchState(store, { permissions });
      },
      hasFallbackForType(typeKey: WorkspaceTypeKey): boolean {
        const user: User | null = activeUserStore.user();
        if (!user) return false;
        return user.workspaces
          .map((ws: UserWorkspace) => ws.workspace)
          .includes(typeKey);
      },
      canAccessWorkspaceType(type: WorkspaceTypeKey): boolean {
        if (this.isSuperAdmin()) return true;
        return this.hasFallbackForType(type);
      },
      hasPermissions(
        workspaceIds: string[],
        actions: keyof Access | Array<keyof Access>
      ): boolean {
        const user = activeUserStore.user();
        if (!user) return false;
        if (user.level > 0 || user.isAdmin || user.isSuperAdmin) return true;
        const permissions = store.permissions();
        let hasPermission = false;

        // Check permissions for each workspace ID
        workspaceIds.forEach(workspaceId => {
          const workspacePermissions: Access = permissions[workspaceId];

          if (workspacePermissions) {
            if (Array.isArray(actions)) {
              const allActionsPermitted = actions.some(
                (action: keyof Access) => {
                  return workspacePermissions[action];
                }
              );
              if (allActionsPermitted) {
                hasPermission = true;
                return;
              }
            } else {
              // If actions is a single key, check if it is present in the permissions
              if (workspacePermissions[actions as keyof Access]) {
                hasPermission = true;
                return;
              }
            }
          }
        });
        return hasPermission;
      },
      isSuperAdmin() {
        const user = activeUserStore.user();
        return user?.isAdmin || user?.isSuperAdmin;
      },
      userWorkspaceIdsByType(type: WorkspaceTypeKey): string[] {
        const userWsIds = activeUserStore
          .user()
          ?.workspaces.map((ws: UserWorkspace) => ws.workspace);
        if (!userWsIds) return [];

        const mapping = store.workspaceTypeMapping();

        return userWsIds.filter((wsId: string) => {
          return mapping[wsId]?.has(type);
        });
      },
      hasPermissionForEntity(
        type: WorkspaceTypeKey,
        entityId: string,
        actions: keyof Access | Array<keyof Access>,
        entityWorkspaceIds: string[] = []
      ): boolean {
        if (this.isSuperAdmin()) return true;

        const workspaceIds = [...entityWorkspaceIds];

        // Add fallback workspace for the type
        workspaceIds.push(type);

        return this.hasPermissions(workspaceIds, actions);
      },
      hasPermissionForType(
        type: WorkspaceTypeKey,
        actions: keyof Access | Array<keyof Access>
      ): boolean {
        if (this.isSuperAdmin()) return true;

        const workspaceIds = this.userWorkspaceIdsByType(type);

        // Add fallback workspace for the type
        workspaceIds.push(type);

        return this.hasPermissions(workspaceIds, actions);
      },
    })
  ),
  withHooks({
    onInit(store) {
      //store.setPermissions();
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
